import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { FormAlert, SelectField, SubmitButton, TextField, type SelectOption } from '../components/form';
import { ModeToggle, type MappingMode } from '../components/mappings/ModeToggle';
import { get, post, statusOf } from '../lib/api';
import { usePageTitle } from '../lib/usePageTitle';

// Office 365 is intentionally omitted (removed from the frontend).
const APP_TYPES = { smartstore: 0, jasper: 1 } as const;

interface AppItem {
    id: string;
    name: string;
    type: string;
    baseUrl: string;
    path: string;
    userCount: number;
}

// Strict client-side validation of the application form (backend contract unchanged):
//   Base-URL must be http(s)://<domain-or-ip>[:port] with NO trailing slash or path.
//   Path must start with "/" (e.g. /smartstore2).
const baseUrlField = z.string().min(1, 'validation.required').regex(/^https?:\/\/[^\s/]+$/, 'validation.baseUrl');
const pathField = z.string().min(1, 'validation.required').regex(/^\/\S+$/, 'validation.path');

const addSchema = z.object({
    type: z.string().min(1, 'validation.required'),
    applicationName: z.string().min(1, 'validation.required'),
    baseUrl: baseUrlField,
    path: pathField,
});
type AddValues = z.infer<typeof addSchema>;

const editSchema = z.object({
    applicationName: z.string().min(1, 'validation.required'),
    baseUrl: baseUrlField,
    path: pathField,
});
type EditValues = z.infer<typeof editSchema>;

export function ApplicationPage() {
    const { t } = useTranslation();
    usePageTitle(t('admin.application.title'));
    const [mode, setMode] = useState<MappingMode>('add');
    const [apps, setApps] = useState<AppItem[]>([]);
    const [editId, setEditId] = useState('');
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [appUsers, setAppUsers] = useState<{ email: string; name: string }[]>([]);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [usersError, setUsersError] = useState('');
    const [showRemoveMappings, setShowRemoveMappings] = useState(false);
    const [removingMappings, setRemovingMappings] = useState(false);

    const addMethods = useForm<AddValues>({
        resolver: zodResolver(addSchema),
        defaultValues: { type: '', applicationName: '', baseUrl: '', path: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const editMethods = useForm<EditValues>({
        resolver: zodResolver(editSchema),
        defaultValues: { applicationName: '', baseUrl: '', path: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const typeOptions = [
        { value: String(APP_TYPES.smartstore), label: t('admin.application.smartstore') },
        { value: String(APP_TYPES.jasper), label: t('admin.application.jasper') },
    ];

    function loadApps() {
        get<AppItem[]>('home/GetApplications')
            .then((r) => setApps(r.data ?? []))
            .catch(() => setServerError(t('admin.application.loadError')));
    }

    useEffect(() => {
        loadApps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedApp = apps.find((a) => a.id === editId);
    const appOptions: SelectOption[] = apps
        .map((a) => ({ value: a.id, label: a.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nb'));

    function switchMode(next: MappingMode) {
        setMode(next);
        setServerError('');
        setSuccess('');
        setEditId('');
        setConfirmText('');
        setAppUsers([]);
        setSelectedEmails([]);
        addMethods.reset({ type: '', applicationName: '', baseUrl: '', path: '' });
        editMethods.reset({ applicationName: '', baseUrl: '', path: '' });
    }

    function loadAppUsers(id: string) {
        setSelectedEmails([]);
        setUsersError('');
        get<{ email: string; name: string }[]>(`home/GetApplicationUsers?id=${id}`)
            .then((r) => setAppUsers(r.data ?? []))
            .catch(() => {
                setAppUsers([]);
                setUsersError(t('admin.application.loadUsersError'));
            });
    }

    function selectEditApp(id: string) {
        setEditId(id);
        setSuccess('');
        setServerError('');
        const app = apps.find((a) => a.id === id);
        if (!app) {
            setAppUsers([]);
            setSelectedEmails([]);
            return;
        }
        editMethods.reset({ applicationName: app.name, baseUrl: app.baseUrl, path: app.path });
        loadAppUsers(id);
    }

    async function onAdd(values: AddValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('home/addApplication', {
                userName: '',
                applicationName: values.applicationName,
                baseUrl: values.baseUrl,
                path: values.path,
                type: Number(values.type),
            });
            setSuccess(t('admin.application.added'));
            addMethods.reset({ type: '', applicationName: '', baseUrl: '', path: '' });
            loadApps();
        } catch (err) {
            setServerError(
                statusOf(err) === 409 ? t('admin.application.nameExists') : t('admin.application.error'),
            );
        }
    }

    async function onUpdate(values: EditValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('home/UpdateApplication', {
                id: editId,
                applicationName: values.applicationName,
                baseUrl: values.baseUrl,
                path: values.path,
            });
            setSuccess(t('admin.application.updated'));
            loadApps();
        } catch (err) {
            setServerError(
                statusOf(err) === 409 ? t('admin.application.nameExists') : t('admin.application.updateError'),
            );
        }
    }

    async function confirmDelete() {
        setDeleting(true);
        setServerError('');
        try {
            await post('home/DeleteApplication', { id: editId });
            setShowDelete(false);
            setConfirmText('');
            setSuccess(t('admin.application.deleted'));
            setEditId('');
            setAppUsers([]);
            setSelectedEmails([]);
            editMethods.reset({ applicationName: '', baseUrl: '', path: '' });
            loadApps();
        } catch (err) {
            setShowDelete(false);
            setConfirmText('');
            setServerError(
                statusOf(err) === 409
                    ? t('admin.application.deleteHasUsers')
                    : t('admin.application.deleteError'),
            );
        } finally {
            setDeleting(false);
        }
    }

    async function confirmRemoveMappings() {
        setRemovingMappings(true);
        setServerError('');
        try {
            const count = selectedEmails.length;
            await post('home/DeleteApplicationMappings', { applicationId: editId, emails: selectedEmails });
            setShowRemoveMappings(false);
            setSuccess(t('admin.application.mappingsRemoved', { count }));
            loadAppUsers(editId);
            loadApps();
        } catch {
            setShowRemoveMappings(false);
            setServerError(t('admin.application.removeMappingsError'));
        } finally {
            setRemovingMappings(false);
        }
    }

    const hasUsers = !!selectedApp && selectedApp.userCount > 0;

    return (
        <>
            <PageHeading title={t('admin.application.title')} description={t('admin.application.description')} />

            <Card body className="shadow-sm" style={{ maxWidth: '36rem' }}>
                <ModeToggle mode={mode} onChange={switchMode} idPrefix="application" />

                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
                <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>

                {mode === 'add' ? (
                    <FormProvider {...addMethods}>
                        <Form noValidate onSubmit={addMethods.handleSubmit(onAdd)}>
                            <SelectField name="type" label={t('admin.application.type')} options={typeOptions} required />
                            <TextField
                                name="applicationName"
                                label={t('admin.application.name')}
                                placeholder={t('admin.application.namePlaceholder')}
                                required
                            />
                            <TextField
                                name="baseUrl"
                                label={t('admin.application.baseUrl')}
                                placeholder={t('admin.application.baseUrlPlaceholder')}
                                required
                            />
                            <TextField
                                name="path"
                                label={t('admin.application.path')}
                                placeholder={t('admin.application.pathPlaceholder')}
                                required
                            />
                            <SubmitButton loading={addMethods.formState.isSubmitting}>
                                {t('admin.application.register')}
                            </SubmitButton>
                        </Form>
                    </FormProvider>
                ) : (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="app-edit-select">{t('admin.application.selectApp')}</Form.Label>
                            <Select
                                inputId="app-edit-select"
                                options={appOptions}
                                classNamePrefix="dkn-rs"
                                placeholder={t('common.selectPlaceholder')}
                                noOptionsMessage={() => t('common.noOptions')}
                                value={appOptions.find((o) => o.value === editId) ?? null}
                                onChange={(o) => selectEditApp((o as SelectOption | null)?.value ?? '')}
                            />
                        </Form.Group>

                        {selectedApp && (
                            <FormProvider {...editMethods}>
                                <Form noValidate onSubmit={editMethods.handleSubmit(onUpdate)}>
                                    <p className="text-body-secondary small mb-3">
                                        {t('admin.application.typeLabel')}: {selectedApp.type}
                                        {' · '}
                                        {t('admin.application.attachedUsers', { count: selectedApp.userCount })}
                                    </p>
                                    <TextField name="applicationName" label={t('admin.application.name')} required />
                                    <TextField name="baseUrl" label={t('admin.application.baseUrl')} required />
                                    <TextField name="path" label={t('admin.application.path')} required />
                                    <div className="d-flex gap-2">
                                        <SubmitButton loading={editMethods.formState.isSubmitting}>
                                            {t('admin.application.update')}
                                        </SubmitButton>
                                        <Button
                                            variant="outline-danger"
                                            type="button"
                                            onClick={() => {
                                                setConfirmText('');
                                                setShowDelete(true);
                                            }}
                                        >
                                            {t('admin.application.delete')}
                                        </Button>
                                    </div>
                                </Form>
                            </FormProvider>
                        )}

                        {selectedApp && (
                            <div className="mt-4 pt-3 border-top">
                                <h3 className="h6 mb-1">{t('admin.application.mappedUsersTitle')}</h3>
                                <p className="text-body-secondary small">
                                    {t('admin.application.mappedUsersHint')}
                                </p>
                                {usersError && (
                                    <Alert variant="danger" className="py-2">
                                        {usersError}
                                    </Alert>
                                )}
                                {appUsers.length === 0 ? (
                                    <p className="text-body-secondary small mb-0">
                                        {t('admin.application.noMappedUsers')}
                                    </p>
                                ) : (
                                    <>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                id="app-users-selectall"
                                                className="mb-0"
                                                label={t('admin.application.selectAll')}
                                                checked={selectedEmails.length === appUsers.length}
                                                onChange={(e) =>
                                                    setSelectedEmails(
                                                        e.target.checked ? appUsers.map((u) => u.email) : [],
                                                    )
                                                }
                                            />
                                            <span className="text-body-secondary small">
                                                {t('admin.application.attachedUsers', { count: appUsers.length })}
                                            </span>
                                        </div>
                                        <div
                                            className="border rounded mb-2"
                                            style={{ maxHeight: '18rem', overflowY: 'auto' }}
                                        >
                                            {appUsers.map((u, i) => (
                                                <label
                                                    key={u.email}
                                                    className={`d-flex align-items-center gap-2 px-3 py-2 mb-0${
                                                        i < appUsers.length - 1 ? ' border-bottom' : ''
                                                    }`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input flex-shrink-0 m-0"
                                                        checked={selectedEmails.includes(u.email)}
                                                        onChange={(e) =>
                                                            setSelectedEmails((prev) =>
                                                                e.target.checked
                                                                    ? [...prev, u.email]
                                                                    : prev.filter((x) => x !== u.email),
                                                            )
                                                        }
                                                    />
                                                    <span className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <span className="d-block text-truncate">{u.email}</span>
                                                        {u.name?.trim() && (
                                                            <span className="d-block text-truncate text-body-secondary small">
                                                                {u.name}
                                                            </span>
                                                        )}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            disabled={selectedEmails.length === 0}
                                            onClick={() => setShowRemoveMappings(true)}
                                        >
                                            {t('admin.application.removeSelected', {
                                                count: selectedEmails.length,
                                            })}
                                        </Button>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Card>

            <ConfirmDialog
                show={showDelete}
                title={t('admin.application.deleteTitle')}
                confirmLabel={t('admin.application.proceed')}
                onCancel={() => setShowDelete(false)}
                onConfirm={confirmDelete}
                confirmDisabled={deleting || hasUsers || confirmText !== selectedApp?.name}
            >
                {hasUsers ? (
                    <Alert variant="warning" className="mb-0">
                        {t('admin.application.deleteHasUsers')}
                    </Alert>
                ) : (
                    <>
                        <p>{t('admin.application.confirmDelete')}</p>
                        <Form.Group>
                            <Form.Label htmlFor="app-delete-confirm">
                                {t('admin.application.confirmLabel', { name: selectedApp?.name ?? '' })}
                            </Form.Label>
                            <Form.Control
                                id="app-delete-confirm"
                                value={confirmText}
                                autoComplete="off"
                                onChange={(e) => setConfirmText(e.target.value)}
                            />
                        </Form.Group>
                    </>
                )}
            </ConfirmDialog>

            <ConfirmDialog
                show={showRemoveMappings}
                title={t('admin.application.removeMappingsTitle')}
                confirmLabel={t('admin.application.removeMappingsProceed')}
                onCancel={() => setShowRemoveMappings(false)}
                onConfirm={confirmRemoveMappings}
                confirmDisabled={removingMappings}
            >
                {t('admin.application.confirmRemoveMappings', {
                    count: selectedEmails.length,
                    name: selectedApp?.name ?? '',
                })}
            </ConfirmDialog>
        </>
    );
}
